import React from 'react';
import { useSelector } from 'react-redux';
import { scroller } from 'react-scroll';

import { useIntl } from 'ha/i18n';
import { Typography, units } from '@hbf/dsl';
import { Price, PriceRange } from 'ha/components/Listing/Price';
import { getUtilityBillsFromCosts } from 'ha/models/ListingUtilityBills/getUtilityBillsFromCosts';
import { makeStyles } from 'tss-react/mui';
import { PricingStructure } from 'ha/modules/Pricing';
import { ListingPricingType } from '@ha/api/v2/types/Listing';

import { FromPrice } from 'ha/components/Listing/Price/FromPrice';
import {
  getPrice,
  getCurrency,
  getCosts,
  getRentalConditions,
  getDesiredDates,
  getPricingValues,
  getPricingType,
  getListingIsCharitable,
  getListingIsFree,
  getIsBelongsToPartner,
} from '../../selectors';

import { useLocalPricing } from '../../hooks/useLocalPricing';
import { useDesiredDates } from '../../hooks/useDesiredDates';

interface Props {
  variant: 'desktop-gallery' | 'desktop-appbar' | 'mobile';
}
const useStyles = makeStyles()(theme => ({
  addDatesLink: {
    fontWeight: 'bold',
  },
  addDatesText: {
    fontSize: units.rem(0.88),
  },
  charityText: {
    [theme.breakpoints.down('lg')]: {
      fontSize: units.rem(1.5),
    },
  },
}));

const ListingPrice: React.FC<Props> = ({ variant }) => {
  const { T } = useIntl();
  const { classes } = useStyles();
  const price = useSelector(getPrice);
  const currency = useSelector(getCurrency);
  const { costs } = useSelector(getCosts);
  const rentalConditions = useSelector(getRentalConditions);
  const desiredDates = useSelector(getDesiredDates);
  const pricingValues = useSelector(getPricingValues);
  const pricingType = useSelector(getPricingType);
  const isCharitable = useSelector(getListingIsCharitable);
  const isFreeListing = useSelector(getListingIsFree);
  const isBelongsToPartner = useSelector(getIsBelongsToPartner);
  const billsType = React.useMemo(
    () => getUtilityBillsFromCosts(costs),
    [costs],
  );

  const { pricingStructure } = useLocalPricing();

  const { hasDesiredDates } = useDesiredDates();

  const onBillsClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      scroller.scrollTo('rental-conditions', {
        smooth: true,
        duration: 500,
        offset: -28,
      });
    },
    [],
  );

  const showPriceRange =
    pricingStructure === PricingStructure.instalment &&
    hasDesiredDates &&
    pricingType === ListingPricingType.MONTHLY;

  const showFromPrice =
    pricingStructure === PricingStructure.instalment &&
    !hasDesiredDates &&
    !isBelongsToPartner &&
    pricingType === ListingPricingType.MONTHLY;

  const allPrices = Object.values(pricingValues.monthly ?? {});
  const minPrice = Math.min(...allPrices);

  if (isCharitable && isFreeListing) {
    return (
      <Typography.H2
        className={classes.charityText}
        component="p"
        data-test-locator="Listing/Price/Price"
        color="mint"
      >
        {T('Free')}
      </Typography.H2>
    );
  }

  if (showFromPrice) {
    return (
      <FromPrice
        billsType={billsType}
        currency={currency}
        minPrice={minPrice}
        onBillsClick={variant === 'desktop-gallery' ? onBillsClick : undefined}
      />
    );
  }

  if (showPriceRange) {
    return (
      <PriceRange
        pricingValues={pricingValues}
        currency={currency}
        billsType={billsType}
        onBillsClick={variant === 'desktop-gallery' ? onBillsClick : undefined}
        desiredDates={desiredDates}
      />
    );
  }

  return (
    <Price
      price={price}
      currency={currency}
      billsType={billsType}
      onBillsClick={variant === 'desktop-gallery' ? onBillsClick : undefined}
      pricingStructure={pricingStructure}
      firstInstalment={rentalConditions?.firstInstalment}
    />
  );
};

export { ListingPrice };
